import { DatePipe, TitleCasePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { Assessment, IntakeForm, Patient } from 'src/API';
import { AssessmentService } from 'src/app/core/api/assessment.service';
import { ClinicService } from 'src/app/core/api/clinic.service';
import { compareDateStrings, parseDateToString } from 'src/app/core/api/date.util';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { ModelUtils } from 'src/app/core/api/model-utils';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { SideNavActionsService } from 'src/app/core/api/sidenav-actions.service';
import { FilterGroup, MatchType } from 'src/app/filter/filter.model';
import { FilterService } from 'src/app/filter/filter.service';
import { IntakeFormService } from 'src/app/intake-form/intake-form.service';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { PatientsPaginatorService } from 'src/app/patients/patients-dashboard/patients-paginator.service';
import { QuestionnaireScorePipe } from 'src/app/shared/shared-pipes/questionnaire-score.pipe';
import { QuestionnaireRiskPipe } from '../../shared/shared-pipes/questionnaire-risk.pipe';
import { FilterType } from './../../../API';
@Component({
  selector: 'csi-widgets-view',
  templateUrl: './widgets-view.component.html',
  styleUrls: ['./widgets-view.component.scss'],
  providers: [DatePipe, QuestionnaireScorePipe, QuestionnaireRiskPipe]
})
export class WidgetsViewComponent implements OnInit {
  cards: any[] = [];
  isLoading = true;
  intakeForms: IntakeForm[];
  patients$ = new BehaviorSubject<Partial<Patient>[]>([]);
  staff: any[] = [];
  assessments: any[] = [];
  recentlyCompletedQuestionnaires: any[] = [];
  questionnairesPendingCompletion: any[] = [];

  private readonly cardTemplates = [
    { id: 'completed', title: 'Recently Completed Questionnaires', colour: 'blue' },
    { id: 'pending', title: 'Questionnaires Pending Completion', colour: 'blue' },
    { id: 'patients', title: 'Recent Patients', colour: 'green' },
    { id: 'assessments', title: 'Recent Assessments', colour: 'orange' },
    { id: 'intake', title: 'Latest Intake Forms', colour: 'purple' },
    { id: 'staff', title: 'Clinic Staff', colour: 'red' }
  ];

  public readonly lastAssessmentKey = 'lastAssessment';

  constructor(
    private clinicSetupService: ClinicSetupService,
    private intakeFormService: IntakeFormService,
    public router: Router,
    private clinicService: ClinicService,
    private patientsPaginatorService: PatientsPaginatorService,
    public questionnaireRequestService: QuestionnaireRequestService,
    public sideNavActionsService: SideNavActionsService,
    private assessmentService: AssessmentService,
    public filterService: FilterService,
    private datePipe: DatePipe,
    public questionnaireScorePipe: QuestionnaireScorePipe,
    public questionnaireRiskPipe: QuestionnaireRiskPipe,
    private titleCasePipe: TitleCasePipe
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    this.cards = this.cardTemplates;
    await Promise.all([
      this.getStaff(),
      this.getIntakeForms(),
      this.getPatients(),
      this.getAssessments(),
      this.getQuestionnairesPendingCompletion(),
      this.getRecentlyCompletedQuestionnaires()
    ]);
    this.cards = this.generateCards();
    this.isLoading = false;
  }

  ngOnDestroy() {}

  private async getIntakeForms(): Promise<void> {
    const data = await this.intakeFormService
      .getIntakeForms(this.clinicSetupService.clinicId)
      .toPromise();
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    this.intakeForms = data.reduce((acc, intakeForm) => {
      if (new Date(intakeForm.createdAt) < monthAgo) return acc;
      let response =
        typeof intakeForm.response === 'string'
          ? safeJsonParse(intakeForm.response)
          : intakeForm.response;

      if (response['questionnaire'] && response['questionnaire'].response) {
        response = {
          ...response,
          scores: response['questionnaire'].response.scores
        };
        intakeForm = {
          ...intakeForm,
          response
        };
      } else {
        const scores =
          typeof intakeForm.response === 'string'
            ? safeJsonParse(response['scores'])
            : safeJsonParse(response['scores']);
        const newResponse = { ...response, scores };
        intakeForm = { ...intakeForm, response: newResponse };
      }

      // Checks for legacy forms and transforms them into the new format
      if (response['patientInformation']) {
        const patientInformation = response['patientInformation'];
        const answers = { patientInformation };
        const newResponse = { ...response, answers };
        intakeForm = { ...intakeForm, response: newResponse };
      }

      // One instance of this, added for compatibility
      if (response['patientDemographics']) {
        const newResponse = {
          ...response,
          answers: { patientInformation: response['patientDemographics'] }
        };
        intakeForm = { ...intakeForm, response: newResponse };
      }

      acc.push(intakeForm);
      return acc;
    }, []);
  }

  private capitalizeName(name: string): string {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  }

  private async getPatients(): Promise<void> {
    const patients = await this.patientsPaginatorService
      .loadNextPatients(0)
      .pipe(untilDestroyed(this))
      .toPromise();

    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    const recentPatients = patients.items.filter(p => new Date(p.updatedAt) >= monthAgo);

    recentPatients.forEach(p => {
      p.firstName = this.capitalizeName(p.firstName);
      p.lastName = this.capitalizeName(p.lastName);
    });

    this.setLastAssessment(recentPatients);
    this.patients$.next(recentPatients);
  }

  private async getQuestionnairesPendingCompletion(): Promise<void> {
    const filterValue =
      this.clinicSetupService.clinicSetupFormGroup.controls.questionnaireExpiry.value || 30;

    let questionnaires = await this.filterService
      .queryFilterData(FilterType.questionnaireRequest, [
        this.generateFilterGroup('createdAt', filterValue)
      ])
      .pipe(untilDestroyed(this))
      .toPromise();
    questionnaires = questionnaires
      .sort((a, b) => compareDateStrings(a.createdAt, b.createdAt))
      .filter(q => !q.completedAt);
    const requests = questionnaires.map(val =>
      this.questionnaireRequestService.getQuestionnaireRequest(val.id)
    );
    if (requests.length === 0) {
      this.questionnairesPendingCompletion = [];
      return;
    }

    try {
      const responses = await forkJoin(requests)
        .pipe(untilDestroyed(this))
        .toPromise();

      this.questionnairesPendingCompletion = responses
        .map((val, i) => ({
          ...val,
          questionnaireRequestPatientId: questionnaires[i].questionnaireRequestPatientId
        }))
        .sort((a, b) => compareDateStrings(b.createdAt, a.createdAt));
    } catch (error) {
      console.error(error);
      this.questionnairesPendingCompletion = [];
    }
  }

  private async getRecentlyCompletedQuestionnaires(): Promise<void> {
    try {
      const questionnaires = await this.filterService
        .queryFilterData(FilterType.questionnaireRequest, [
          this.generateFilterGroup('completedAt', 30)
        ])
        .pipe(untilDestroyed(this))
        .toPromise();

      questionnaires.sort((a, b) => compareDateStrings(b.completedAt, a.completedAt));
      const requests = questionnaires.map(val =>
        this.questionnaireRequestService.getQuestionnaireRequest(val.id)
      );
      if (requests.length === 0) {
        this.recentlyCompletedQuestionnaires = [];
        return;
      }

      const responses = await forkJoin(requests)
        .pipe(untilDestroyed(this))
        .toPromise();

      this.recentlyCompletedQuestionnaires = responses
        .map((val, i) => ({
          ...val,
          questionnaireRequestPatientId: questionnaires[i].questionnaireRequestPatientId
        }))
        .sort((a, b) => compareDateStrings(b.completedAt, a.completedAt));
    } catch (error) {
      console.error(error);
      this.recentlyCompletedQuestionnaires = [];
    }
  }

  private generateFilterGroup(field: string, timeFrameInDays: number): FilterGroup {
    const dateToday = new Date();

    // Calculate the date for two weeks ago
    const startDate = new Date();
    startDate.setDate(dateToday.getDate() - timeFrameInDays * 2);

    return {
      type: MatchType.and,
      filters: [
        {
          field,
          operator: 'greaterThanOrEqualTo',
          searchValue: startDate.toISOString()
        },
        {
          field,
          operator: 'lessThanOrEqualTo',
          searchValue: dateToday.toISOString()
        }
      ]
    };
  }

  private async getAssessments(): Promise<void> {
    const dateToday = new Date();
    const startDate = new Date();
    startDate.setDate(dateToday.getDate() - 90);

    try {
      const data = await this.assessmentService
        .listAssessmentsByDateRange([parseDateToString(startDate), parseDateToString(dateToday)])
        .pipe(untilDestroyed(this))
        .toPromise();

      this.assessments = data
        .map(assessment => {
          const parsedBody = safeJsonParse(assessment.body);
          const method =
            parsedBody && parsedBody.dryEyeForm && parsedBody.dryEyeForm.assessmentMethod;

          const friendlyType = this.assessmentService.encounterMethodFriendlyName[method] || method;

          return { ...assessment, body: parsedBody, type: friendlyType };
        })
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } catch (error) {
      this.assessments = [];
    }
  }

  private async getStaff(): Promise<void> {
    const data = await this.clinicService
      .getClinicStaff(this.clinicSetupService.clinicId)
      .toPromise();
    const order = { 'Doctor / Owner': 1, Doctor: 2, Tech: 3, Receptionist: 4 };
    this.staff = data
      .filter(staff => !staff.deactivated)
      .map(staff => ({
        ...staff,
        staffType: staff.staffType || 'Doctor / Owner'
      }))
      .sort((a, b) => (order[a.staffType] || 5) - (order[b.staffType] || 5));
  }

  private setLastAssessment(patients: Partial<Patient>[]) {
    patients.forEach(patient => {
      patient[this.lastAssessmentKey] = ModelUtils.sortByDate(
        patient.assessments.items,
        'createdAt'
      )[0];
    });
  }

  private concatName = val =>
    this.titleCasePipe.transform(val.firstName.split(' ')[0] + ' ' + val.lastName);

  getCachedPatientName(id: string) {
    const cachedPatient = this.filterService.filterTypeCachedFilterMap['patient'].data[id];

    if (!cachedPatient) {
      return '';
    }

    let patient = {
      firstName: this.filterService.filterTypeCachedFilterMap['patient'].data[id].firstName,
      lastName: this.filterService.filterTypeCachedFilterMap['patient'].data[id].lastName
    };
    return this.concatName(patient);
  }

  private generateCards(): any[] {
    return this.cardTemplates.map(template => {
      switch (template.id) {
        case 'completed':
          return {
            ...template,
            content: [
              {
                heading: 'Name',
                transform: (val: any) =>
                  this.getCachedPatientName(val.questionnaireRequestPatientId)
              },
              {
                heading: 'Type',
                transform: (val: any) =>
                  this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap[val.type]
              },
              {
                heading: 'Completed On',
                transform: (val: any) => this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
              }
            ],
            clickable: {
              action: (val: Patient) =>
                this.router.navigateByUrl('patients/' + val['questionnaireRequestPatientId'])
            },
            data: this.recentlyCompletedQuestionnaires
          };
        case 'pending':
          return {
            ...template,
            content: [
              {
                heading: 'Name',
                transform: (val: any) =>
                  this.getCachedPatientName(val.questionnaireRequestPatientId)
              },
              {
                heading: 'Type',
                transform: (val: any) =>
                  this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap[val.type]
              },
              {
                heading: 'Date Sent',
                transform: (val: any) => this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
              }
            ],
            clickable: {
              action: (val: Patient) =>
                this.router.navigateByUrl('patients/' + val['questionnaireRequestPatientId'])
            },
            data: this.questionnairesPendingCompletion
          };
        case 'patients':
          return {
            ...template,
            expandedTitle: 'Patients for the last month',
            content: [
              {
                heading: 'Name',
                transform: (val: IntakeForm) => this.concatName(val)
              },
              {
                heading: 'Updated On',
                transform: (val: Patient) => this.datePipe.transform(val.updatedAt, 'MMM dd, yyyy')
              }
            ],
            expandedContent: [
              {
                heading: 'First Name',
                transform: (val: IntakeForm) => val['firstName']
              },
              {
                heading: 'Last Name',
                transform: (val: IntakeForm) => val['lastName']
              },
              {
                heading: 'Last Updated',
                transform: (val: Patient) =>
                  this.datePipe.transform(val.updatedAt, 'MMM dd, yyyy HH:mm')
              }
            ],
            clickable: {
              action: (val: Patient) => this.router.navigateByUrl('patients/' + val.id)
            },
            buttons: [
              {
                icon: 'open_in_new',
                tooltip: 'Open Patient Management',
                action: () => this.router.navigateByUrl('patients')
              }
            ],
            data: this.patients$.getValue()
          };
        case 'assessments':
          return {
            ...template,
            content: [
              {
                heading: 'Name',
                transform: (val: any) => this.concatName(val.patient)
              },
              {
                heading: 'Type',
                item: 'type'
              },
              {
                heading: 'Completed On',
                transform: (val: any) => this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
              }
            ],
            clickable: {
              action: (val: Assessment) =>
                this.router.navigateByUrl(
                  'patients/' + val.patient.id + '/(encounters:' + val.id + ')'
                )
            },
            data: this.assessments
          };
        case 'intake':
          return {
            ...template,
            expandedTitle: 'Intake Forms for the last month',
            content: [
              {
                heading: 'Name',
                transform: (val: IntakeForm) =>
                  this.concatName(val.response['answers'].patientInformation)
              },
              {
                item: 'createdAt',
                heading: 'Completed On',
                transform: (val: IntakeForm) =>
                  this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
              },
              {
                heading: 'Score',
                transform: (val: any) =>
                  this.questionnaireRiskPipe.transform(this.questionnaireScorePipe.transform(val))
              }
            ],
            expandedContent: [
              {
                heading: 'First Name',
                transform: (val: IntakeForm) => val.response['answers'].patientInformation.firstName
              },
              {
                heading: 'Last Name',
                transform: (val: IntakeForm) => val.response['answers'].patientInformation.lastName
              },
              {
                item: 'createdAt',
                heading: 'Completed On',
                transform: (val: IntakeForm) =>
                  this.datePipe.transform(val.createdAt, 'MMM dd, yyyy')
              },
              {
                heading: 'Score',
                transform: (val: any) =>
                  this.questionnaireRiskPipe.transform(this.questionnaireScorePipe.transform(val))
              }
            ],
            buttons: [
              {
                icon: 'qr_code',
                tooltip: 'Intake Form QR Code',
                action: () =>
                  this.sideNavActionsService.openClinicSetupModal({
                    selectedTabLabel: 'Intake Form'
                  })
              },
              {
                icon: 'open_in_new',
                tooltip: 'Open Intake Forms',
                action: () => this.router.navigateByUrl('intake-forms')
              }
            ],
            clickable: {
              action: (val: IntakeForm) => this.router.navigateByUrl('intake-forms/' + val.id)
            },
            data: this.intakeForms
          };
        case 'staff':
          return {
            ...template,
            content: [
              {
                heading: 'Name',
                transform: (val: IntakeForm) => this.concatName(val)
              },
              { item: 'staffType', heading: 'Position' }
            ],
            buttons: [
              {
                icon: 'open_in_new',
                tooltip: 'Open Staff Management',
                action: () =>
                  this.sideNavActionsService.openClinicSetupModal({
                    selectedTabLabel: 'Clinic Staff'
                  })
              }
            ],
            data: this.staff
          };
        default:
          return template;
      }
    });
  }
}
